/* eslint-disable react/no-unknown-property */
import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import InputMask from 'react-input-mask'
import { Box, Text, Flex, HStack, Checkbox, Select, FormLabel, FormErrorMessage, FormControl, Input, Button, useDisclosure, Skeleton, FormHelperText } from '@chakra-ui/react'
import { ChevronRightIcon, ChevronLeftIcon } from '@chakra-ui/icons'

import settingValidateCompany from 'utils/validation/settingValidateCompany'
import { isSuccessStatus } from 'utils/functions/validations'
import { getInformationCompanyAfip, saveCompany, updateCompany, validateCompany } from 'services/api'
import backright from 'assets/images/backright.png'
import IBalert from 'components/common/IBalert/IBalert'
import { RegisterCompanyProps } from 'views/Company'
import { useGlobalContext } from 'providers/GlobalProvider'
import { ICompany, IFetchedValidatedCompany } from 'services/IApi.d'
import useGa from 'hooks/ga/useGa'
import { ISendDataLayer } from 'hooks/ga/interface.d'
import { IFormValues, IInformationCompany } from './FormRegisterCompany.d'
import { handleNumericInput } from 'utils/functions/formatNumber'

interface FCuit {
  cuit: string
}

const FormRegisterCompany: React.FC<RegisterCompanyProps> = ({ ...initialData }) => {
  const navigate = useNavigate()
  const { sendDataLayer } = useGa()
  const [activityCodeId, setActivityCodeId] = useState<string>('')
  const { idSuscriptor, isRequestSent, setIdEmpresa, setCompanyQuantity } = useGlobalContext()
  const { isEditable, cuit, codigoIIBB: initialCodigoIIBB, isLoaded, categoryIva, categoryIibb, provinces, informationCompany } = initialData

  const ErrorPreset = {
    alertErrorGetCuit: {
      title: 'Algo anda mal',
      text: 'En estos momentos presentamos inconveniente para completar tus datos, por favor completálos manualmente',
      variant: 'sm',
    },
    alertError500: {
      title: 'No podemos validar el CUIT',
      text: 'Estamos trabajando para solucionarlo, volvé a intentar más tarde.',
      variant: 'sm',
    },
    alertErrorPostRegister: {
      title: 'Algo anda mal',
      text: 'En estos momentos presentamos inconvenientes en la plataforma, por favor intentá nuevamente en unos minutos',
      variant: 'sm',
    },
    alertErrorCompanyExist: {
      title: 'No podemos continuar',
      text: 'El CUIT ya está registrado en Interbanking. Intentá con otro.',
      variant: 'sm',
    },
  }
  const {
    handleSubmit: handleSubmitCuit,
    register: registerCuit,
    setValue: setCuitValue,
    getValues: getValuesCuit,
    clearErrors: clearErrorsCuit,
    watch: watchCuit,
    formState: {
      errors: errorsCuit,
      isSubmitting: isSubmittingCuit,
      isDirty: isDirtyCuit,
      isValid: isValidCuit
    }
  } = useForm<FCuit>({ mode: 'onBlur', reValidateMode: 'onBlur', defaultValues: { cuit } })
  const [oldCuit, setOldCuit] = useState<string>('')
  const [stateResponse, setStateResponse] = useState<boolean>(true)
  const [showAlertError, setShowAlertError] = useState<boolean>(false)
  const [typeError, setTypeError] = useState<string>('')
  const {
    handleSubmit,
    register,
    setValue,
    unregister,
    watch,
    getValues,
    clearErrors,
    formState: {
      errors,
      isSubmitting,
      isDirty,
      isValid
    }
  } = useForm<RegisterCompanyProps>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUnregister: false,
    defaultValues: initialData.informationCompany
  })
  const [isDisabledCodigoIIBB, setIsDisabledCodigoIIBB] = useState<boolean | null>()
  const [maskCodigoIIBB, setMaskCodigoIIBB] = useState<string | null>()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const resetValidationCuit = e => clearErrorsCuit([e.target.name])
  const resetValidation = e => clearErrors([e.target.name])
  const handleOpenPostalPage = () => {
    window.open('https://www.correoargentino.com.ar/formularios/cpa', '_blank', 'noopener')
  }
  const onSubmitCuit = async (values: { cuit: string }) => {
    const cuitNoDash = values.cuit.replace(/-/gi, '')
    const { status: statusValidate, data: dataValidate }: IFetchedValidatedCompany = await validateCompany(cuitNoDash)

    if (statusValidate !== 200 && statusValidate !== 201) {
      setTypeError('alertError500')
      setShowAlertError(true)
      onOpen()
      return
    }

    if (dataValidate.exists) {
      setTypeError('alertErrorCompanyExist')
      setShowAlertError(true)
      onOpen()
      return
    }

    const { data, status }: IInformationCompany = await getInformationCompanyAfip(cuitNoDash)
    setOldCuit(getValuesCuit('cuit'))
    setStateResponse(false)
    const event: ISendDataLayer = {
      event: 'completed_onboarding_step',
      onboarding_step: 'Validación CUIT empresa',
    }
    if (status === 200 && data) {
      sendDataLayer(event)
      setValue('informationCompany.businessName', data.razonSocial, { shouldValidate: true })
      setValue('informationCompany.postalCode', data.cp, { shouldValidate: true })
      setValue('informationCompany.street', data.calle, { shouldValidate: true })
      setActivityCodeId(data.codigoActividadId)
    } else {
      setShowAlertError(true)
      setTypeError('alertErrorGetCuit')
      onOpen()
    }
  }

  const onSubmit = async () => {
    const formCuit: string = getValuesCuit('cuit')
    register('cuit')
    setValue('cuit', formCuit)
    const values: IFormValues = { ...getValues() }
    const informationCompanyForm: ICompany = values.informationCompany
    const splittedStreet: string[] = informationCompanyForm.street.split(' ')
    const streetNumber: string = splittedStreet.pop()
    const cuitNoDash: string = values.cuit.replace(/-/gi, '')

    const companyInformation: ICompany = {
      street: splittedStreet.join(' '),
      codeIibb: informationCompanyForm.codeIibb,
      categoryIibbId: informationCompanyForm.categoryIibbId,
      postalCode: informationCompanyForm.postalCode,
      cuit: cuitNoDash,
      dept: informationCompanyForm.dept,
      ivaId: informationCompanyForm.ivaId,
      locality: informationCompanyForm.locality,
      number: streetNumber,
      floor: informationCompanyForm.floor,
      prefix: '',
      provinceId: informationCompanyForm.provinceId,
      businessName: informationCompanyForm.businessName,
      activityCodeId,
      iibbAgentPerception: informationCompanyForm.iibbAgentPerception,
      ivaAgentRetention: informationCompanyForm.ivaAgentRetention,
      subscriberId: idSuscriptor,
    }

    let isSuccessful: boolean = false
    const event: ISendDataLayer = {
      event: 'completed_onboarding_step',
      onboarding_step: 'Cargar datos de empresa',
    }
    if (isEditable) {
      const { status } = await updateCompany({ ...companyInformation, id: informationCompany.id })
      isSuccessful = isSuccessStatus(status)
      event.first_time = 'No'
    } else {
      const { status, data: { companyId } } = await saveCompany(companyInformation)
      setIdEmpresa(companyId)
      isSuccessful = isSuccessStatus(status)
      event.first_time = 'Si'
    }

    if (!isSuccessful) {
      setShowAlertError(true)
      setTypeError('alertErrorPostRegister')
      onOpen()
    }
    if (isSuccessful) {
      sendDataLayer(event)
      navigate('/dashboard')
      setCompanyQuantity(prev => prev + 1)
    }
  }

  // Name of this func is wrong, should return true if all inputs are fill
  const watchAllInputsFill = (): boolean => (
    watch('informationCompany.businessName') === '' ||
    watch('informationCompany.postalCode') === '' ||
    watch('informationCompany.street') === '' ||
    watch('informationCompany.provinceId') === '' ||
    watch('informationCompany.locality') === '' ||
    watch('informationCompany.ivaId') === '' ||
    watch('informationCompany.categoryIibbId') === '' ||
    (watch('informationCompany.categoryIibbId') !== '3' ? watch('informationCompany.codeIibb') === '' : false)
  )

  const isDisabledButtonFCuit = (): boolean => !isDirtyCuit || !isValidCuit || isSubmittingCuit || !stateResponse || isRequestSent
  const isDisabledButtonFRegister = (): boolean => !isDirty || !isValid || isSubmitting || watchAllInputsFill()

  const getTextoNumerosIIBB = (): string => {
    const idCategoriaIIBB = watch('informationCompany.categoryIibbId')
    if (idCategoriaIIBB === '1' && watch('informationCompany.provinceId') === '2') {
      return '11'
    }
    if (idCategoriaIIBB === '2') {
      return '14'
    }
    return '9'
  }
  const getMaskCodigoIIBB = (): string => {
    if (!informationCompany) return ''
    const { categoryIibbId } = informationCompany

    if (categoryIibbId === '1') {
      return '9999999-99'
    }
    if (categoryIibbId === '1' && informationCompany.provinceId === '2') {
      return '99-99999999-9'
    }
    if (categoryIibbId === '2') {
      return '99999999999-999'
    }
    return ''

  }

  useEffect(() => {
    if (informationCompany) {
      const {
        cuit: companyCuit,
        street,
        number,
        codeIibb,
        postalCode,
        businessName,
        provinceId,
        locality,
        floor,
        dept,
        categoryIibbId,
        ivaId,
        iibbAgentPerception,
        ivaAgentRetention,
      } = informationCompany
      const companyStreet = `${street} ${number}`
      setCuitValue('cuit', companyCuit)
      setValue('informationCompany.street', companyStreet, { shouldValidate: true })
      setValue('informationCompany.postalCode', postalCode, { shouldValidate: true })
      setValue('informationCompany.businessName', businessName, { shouldValidate: true })
      setValue('informationCompany.provinceId', provinceId, { shouldValidate: true })
      setValue('informationCompany.locality', locality, { shouldValidate: true })
      setValue('informationCompany.floor', floor, { shouldValidate: true })
      setValue('informationCompany.dept', dept, { shouldValidate: true })
      setValue('informationCompany.iibbAgentPerception', iibbAgentPerception, { shouldValidate: true })
      setValue('informationCompany.ivaAgentRetention', ivaAgentRetention, { shouldValidate: true })
      setValue('informationCompany.categoryIibbId', categoryIibbId, { shouldValidate: true })
      setValue('informationCompany.codeIibb', codeIibb, { shouldValidate: true })
      setValue('informationCompany.ivaId', ivaId, { shouldValidate: true })
    }
  }, [informationCompany])

  useEffect(() => {
    const idCategoriaIIBB = watch('informationCompany.categoryIibbId')
    if (idCategoriaIIBB === '3') {
      if (isDirty) setValue('informationCompany.codeIibb', '')
      unregister('informationCompany.codeIibb')

      setIsDisabledCodigoIIBB(true)
    } else {
      if (idCategoriaIIBB === '1') {
        if (isDirty) setValue('informationCompany.codeIibb', '')
        setMaskCodigoIIBB('9999999-99')
        register('informationCompany.codeIibb', { ...settingValidateCompany.categoryIibbIdcategoryIibbIIBB1 })
      }
      if (idCategoriaIIBB === '1' && watch('informationCompany.provinceId') === '2') {
        if (isDirty) setValue('informationCompany.codeIibb', '')
        setMaskCodigoIIBB('99-99999999-9')
        register('informationCompany.codeIibb', { ...settingValidateCompany.categoryIibbIdcategoryIibbIIBB1BSAS })
      }
      if (idCategoriaIIBB === '2') {
        if (isDirty) setValue('informationCompany.codeIibb', '')
        setMaskCodigoIIBB('99999999999-999')
        register('informationCompany.codeIibb', { ...settingValidateCompany.categoryIibbIdcategoryIibbIIBB2 })
      }
      setIsDisabledCodigoIIBB(false)
    }
  }, [watch('informationCompany.categoryIibbId'), watch('informationCompany.provinceId')])

  useEffect(() => {
    if (!isEditable) {
      const changeCuit = watchCuit('cuit')
      if (oldCuit !== changeCuit) {
        setStateResponse(true)
        setValue('informationCompany.businessName', '')
        setValue('informationCompany.postalCode', '')
        setValue('informationCompany.street', '')
        setValue('informationCompany.provinceId', '')
        setValue('informationCompany.locality', '')
        setValue('informationCompany.floor', '')
        setValue('informationCompany.dept', '')
      }
    } else {
      setStateResponse(false)
    }
  }, [watchCuit('cuit')])

  return (
    <>
      <Box pt='30px'>
        <Box
          bgImage={backright}
          bgRepeat='no-repeat'
          position='absolute'
          bgPosition='right'
          w='calc(100% - 42px)'
          h='100%'
          top='0'
        />
        <form onSubmit={handleSubmitCuit(onSubmitCuit)} data-testid='cuit-form'>
          <HStack spacing='24px' pb='50px'>
            <Box w='229px'>
              <Skeleton isLoaded={isLoaded}>
                <FormControl isInvalid={!!errorsCuit.cuit}>
                  <Input
                    as={InputMask} mask='99-99999999-9'
                    disabled={isEditable}
                    data-testid='cuit-input'
                    id='cuit'
                    maskChar={null}
                    placeholder=' '
                    onFocus={e => resetValidationCuit(e)}
                    {...registerCuit('cuit', settingValidateCompany.cuit)} />
                  <FormLabel textStyle='bodySm'>
                    CUIT
                  </FormLabel>
                  <FormErrorMessage>
                    {errorsCuit?.cuit && errorsCuit.cuit?.message}
                  </FormErrorMessage>
                </FormControl>
              </Skeleton>
            </Box>
            <Box>
              <Skeleton isLoaded={isLoaded}>
                <Button
                  data-testid='cuit-button'
                  disabled={isDisabledButtonFCuit()}
                  isLoading={isSubmittingCuit}
                  type='submit'
                  size='md'
                >
                  Completar información
                </Button>
              </Skeleton>
            </Box>
          </HStack>
        </form>
      </Box>
      <Box>
        <form onSubmit={handleSubmit(onSubmit)} data-testid='register-form'>
          <HStack spacing='24px' pb='45px'>
            <Box w='300px'>
              <Skeleton isLoaded={isLoaded}>
                <FormControl isInvalid={!!errors.informationCompany?.businessName}>
                  <Input
                    disabled={isRequestSent || stateResponse}
                    data-testid='businessName-input'
                    placeholder=' '
                    id='businessName'
                    maxLength={40}
                    onFocus={e => resetValidation(e)}
                    {...register('informationCompany.businessName', settingValidateCompany.businessName)} />
                  <FormLabel textStyle='bodySm'>
                    Razón social
                  </FormLabel>
                  {!errors?.informationCompany?.businessName ?
                    <FormHelperText marginTop={0} position='absolute' left={3} fontSize='12px' color='neutral.110'>
                      Ingresá un máximo de 40 caracteres
                    </FormHelperText>
                    :
                    <FormErrorMessage>{errors.informationCompany?.businessName?.message}</FormErrorMessage>
                  }
                </FormControl>
              </Skeleton>
            </Box>
            <Box w='302px'>
              <Skeleton isLoaded={isLoaded}>
                <FormControl isInvalid={!!errors.informationCompany?.postalCode}>
                  <Input
                  disabled={isRequestSent || stateResponse}
                  data-testid='postalCode-input'
                  placeholder=' '
                  maxLength={4}
                  onInput={e => handleNumericInput(e)}
                  onFocus={e => resetValidation(e)}
                  {...register('informationCompany.postalCode', settingValidateCompany.postalCode)} />
                  <FormLabel textStyle='bodySm'>
                    Código postal
                  </FormLabel>
                  {
                    <Box display='flex' justifyContent='space-around' position='relative' alignItems='baseline'>
                      {
                        errors.informationCompany?.postalCode?.message && (
                          <FormErrorMessage left={3}>{errors.informationCompany?.postalCode?.message}</FormErrorMessage>
                        )
                      }
                    </Box>
                  }

                </FormControl>
              </Skeleton>
            </Box>
          </HStack>
          <HStack spacing='28px' pb='40px'>
            <Box w='300px'>
              <Skeleton isLoaded={isLoaded}>
                <FormControl isInvalid={!!errors.informationCompany?.street}>
                  <Input
                    disabled={isRequestSent || stateResponse}
                    data-testid='street-input'
                    placeholder=' '
                    maxLength={50}
                    onFocus={e => resetValidation(e)}
                    {...register('informationCompany.street', settingValidateCompany.street)} />
                  <FormLabel>
                    Dirección de la compañía
                  </FormLabel>
                  {
                    !(isRequestSent || stateResponse) && <Box display='flex' justifyContent='space-around' position='relative' alignItems='baseline'>
                      {
                        !errors.informationCompany?.street?.message ? (
                          <FormHelperText marginTop={0} position='absolute' left={3} fontSize='12px'>
                            Debe contener texto y al menos un número
                          </FormHelperText>
                        ) : (
                          <FormErrorMessage left={3}> {errors?.informationCompany?.street && errors.informationCompany?.street?.message}</FormErrorMessage>
                        )
                      }
                    </Box>
                  }
                </FormControl>
              </Skeleton>
            </Box>
            <Box w='135px'>
              <Skeleton isLoaded={isLoaded}>
                <FormControl isInvalid={!!errors.informationCompany?.floor}>
                  <Input
                    as={InputMask} mask='99'
                    maskChar={null}
                    disabled={isRequestSent || stateResponse}
                    data-testid='floor-input'
                    maxLength={2}
                    placeholder=' '
                    {...register('informationCompany.floor', settingValidateCompany.floor)}
                    onFocus={e => resetValidation(e)} />
                  <FormLabel left={3}>Piso</FormLabel>
                  <FormErrorMessage>
                    {errors?.informationCompany?.floor && errors.informationCompany?.floor?.message}
                  </FormErrorMessage>
                </FormControl>
              </Skeleton>
            </Box>
            <Box w='140px'>
              <Skeleton isLoaded={isLoaded}>
                <FormControl isInvalid={!!errors.informationCompany?.dept}>
                  <Input
                    disabled={isRequestSent || stateResponse}
                    data-testid='dept-input'
                    placeholder=' '
                    maxLength={4}
                    onFocus={e => resetValidation(e)}
                    {...register('informationCompany.dept', settingValidateCompany.dept)} />
                  <FormLabel>Departamento</FormLabel>
                  <FormErrorMessage>
                    {errors?.informationCompany?.dept && errors.informationCompany?.dept?.message}
                  </FormErrorMessage>
                </FormControl>
              </Skeleton>
            </Box>
          </HStack>
          <HStack spacing='28px' pb='33px'>
            <Box w='300px'>
              <Skeleton isLoaded={isLoaded}>
                <FormControl isInvalid={!!errors.informationCompany?.provinceId}>
                  <Select
                    placeholder='Provincia'
                    data-testid='provinceId-select'
                    disabled={isRequestSent || stateResponse}
                    onFocus={e => resetValidation(e)}
                    {...register('informationCompany.provinceId', settingValidateCompany.provinceId)}
                  >
                    {provinces?.provinciaList.map(province => <option value={province.id} key={province.id}>{province.nombre}</option>)}
                  </Select>
                  {
                    !!watch('informationCompany.provinceId') && <FormLabel>
                      Provincia
                    </FormLabel>
                  }
                  <FormErrorMessage>
                    {errors?.informationCompany?.provinceId && errors.informationCompany?.provinceId?.message}
                  </FormErrorMessage>
                </FormControl>
              </Skeleton>
            </Box>
            <Box w='302px'>
              <Skeleton isLoaded={isLoaded}>
                <FormControl isInvalid={!!errors.informationCompany?.locality}>
                  <Input
                    disabled={isRequestSent || stateResponse}
                    data-testid='locality-input'
                    placeholder=' '
                    maxLength={50}
                    onFocus={e => resetValidation(e)}
                    {...register('informationCompany.locality', settingValidateCompany.locality)} />
                  <FormLabel>
                    Localidad
                  </FormLabel>
                  <FormErrorMessage>
                    {errors?.informationCompany?.locality && errors.informationCompany?.locality?.message}
                  </FormErrorMessage>
                </FormControl>
              </Skeleton>
            </Box>
          </HStack>
          <Text pb='30px' textStyle='titleMdBold'>
            <Skeleton isLoaded={isLoaded}>
              Datos impositivos
            </Skeleton>
          </Text>
          <HStack spacing='24px' pb='33px'>
            <Box w='300px'>
              <Skeleton isLoaded={isLoaded}>
                <FormControl isInvalid={!!errors.informationCompany?.ivaId}>
                  <Select
                    placeholder='Seleccioná categoría'
                    data-testid='ivaId-select'
                    disabled={isRequestSent}
                    onFocus={e => resetValidation(e)}
                    {...register('informationCompany.ivaId', settingValidateCompany.ivaId)}
                  >
                    {categoryIva?.ivaTipoList.map(list => <option value={list.id} key={list.id}>{list.descripcion}</option>)}
                  </Select>
                  <FormLabel>
                    Categoría IVA
                  </FormLabel>
                  <FormErrorMessage>
                    {errors?.informationCompany?.ivaId && errors.informationCompany?.ivaId?.message}
                  </FormErrorMessage>
                </FormControl>
              </Skeleton>
            </Box>
            <Box w='302px'>
              <Skeleton isLoaded={isLoaded}>
                <FormControl>
                  <Checkbox
                    size="md"
                    disabled={isRequestSent}
                    defaultChecked={informationCompany?.ivaAgentRetention}
                    data-testid='ivaAgentRetention-checkbox'
                    {...register('informationCompany.ivaAgentRetention')}
                  >
                    Soy agente de retención de IVA
                  </Checkbox>
                </FormControl>
              </Skeleton>
            </Box>
          </HStack>
          <HStack spacing='24px' pb='33px'>
            <Box w='300px'>
              <Skeleton isLoaded={isLoaded}>
                <FormControl isInvalid={!!errors.informationCompany?.categoryIibbId}>
                  <Select
                    placeholder='Seleccioná categoría'
                    disabled={isRequestSent}
                    data-testid='categoryIibbId-select'
                    onFocus={e => resetValidation(e)}
                    {...register('informationCompany.categoryIibbId', settingValidateCompany.categoryIibbId)}
                  >
                    {categoryIibb?.iibbList.map(category => <option value={category.id} key={category.id}>{category.descripcion}</option>)}
                  </Select>
                  <FormLabel>
                    Categoría IIBB
                  </FormLabel>
                  <FormErrorMessage left={4}> {errors?.informationCompany?.categoryIibbId && errors.informationCompany?.categoryIibbId?.message}</FormErrorMessage>
                </FormControl>
              </Skeleton>
            </Box>
            <Box w='302px'>
              <Skeleton isLoaded={isLoaded}>
                <FormControl isInvalid={!!errors.informationCompany?.codeIibb}>
                  <Input
                    as={InputMask}
                    mask={maskCodigoIIBB || getMaskCodigoIIBB()}
                    maskChar={null}
                    disabled={isRequestSent || isDisabledCodigoIIBB}
                    data-testid='codeIibb-input'
                    placeholder=' '
                    defaultValue={initialCodigoIIBB}
                    onFocus={e => resetValidation(e)}
                    {...register('informationCompany.codeIibb')} />
                  <FormLabel>
                    Nro. de Ingresos brutos
                  </FormLabel>
                  {
                    !(isRequestSent || stateResponse) && watch('informationCompany.provinceId') && <Box display='flex' justifyContent='space-around' position='relative' alignItems='baseline'>
                      {
                        (!errors.informationCompany?.codeIibb?.message && !isDisabledCodigoIIBB) ? (
                          <FormHelperText marginTop={0} position='absolute' left={5}>
                            {`Debe contener ${getTextoNumerosIIBB()} números`}
                          </FormHelperText>
                        ) : (
                          <FormErrorMessage left={4}> {`Debe contener ${getTextoNumerosIIBB()} números`}</FormErrorMessage>
                        )
                      }
                    </Box>
                  }
                </FormControl>
              </Skeleton>
            </Box>
            <Box w='302px'>
              <Skeleton isLoaded={isLoaded}>
                <FormControl>
                  <Checkbox
                    size="md"
                    defaultChecked={informationCompany?.iibbAgentPerception}
                    disabled={isRequestSent}
                    data-testid='iibbAgentPerception-checkbox'
                    {...register('informationCompany.iibbAgentPerception')}
                  >
                    Soy agente de percepción de IIBB
                  </Checkbox>
                </FormControl>
              </Skeleton>
            </Box>
          </HStack>
          <Box position='relative' bgColor='white'>
            <Flex
              boxShadow='0px -3px 8px 4px #00000019'
              justifyContent="space-between"
              h='94px'
              borderTop='solid 1px'
              borderTopColor='grey.30'
              left='-42px'
              p='0 65px'
              w='calc(100% + 84px)'
              position='relative'
              alignItems='center'
            >
              <Button
                variant='primary-text'
                onClick={() => navigate('/dashboard')}
                leftIcon={<ChevronLeftIcon w={6} h={6} />}
              >
                Volver
              </Button>
              <Button
                disabled={isDisabledButtonFRegister()}
                data-testid='submit-button'
                isLoading={isSubmitting}
                type='submit'
                rightIcon={<ChevronRightIcon w={6} h={6} />}
              >
                Guardar información
              </Button>
            </Flex>
          </Box>
        </form>
      </Box>
      {showAlertError &&
        <IBalert
          type='error'
          isOpen={isOpen}
          onClose={onClose}
          title={ErrorPreset[typeError].title}
          text={ErrorPreset[typeError].text}
          variant={ErrorPreset[typeError].variant}
          buttonClose
        />
      }
    </>
  )
}

export default FormRegisterCompany
